/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  updateCustomerLocationEndpoint,
  useGetAddressDetails,
  useGetServeciableStores,
} from './apis';
import { Bike, House, ShoppingCart } from 'lucide-react';
import { MetricsForJumboEvent } from '../../../../analytics/constants';
import {
  CheckStoreServiceabilityInputEvent,
  UpdateCustomerLocationEvent,
} from '../../../../analytics/store-serviceability/events';
import Button from '../../../ComponentsV1/Button';
import { useMutationCall } from '../../../../api/react-client';
import { useClient } from '../../../../api/api-client';
import toast from 'react-hot-toast';
import LocationUpdateConformationUI from './LocationUpdateConformationUI';
import DataCard from '../../../ComponentsV1/DataCard/DataCard';
import { LocationSearchBox } from './LocationSearchBox';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';

const AddressDetails = ({ addressDetails }) => {
  return (
    <div className='tw-flex tw-flex-col tw-gap-6 tw-w-full'>
      <DataCard
        icon={<House size={16} />}
        heading='Current Customer Address'
        stats={[
          {
            value: (
              <span
                className='tw-font-medium tw-text-zinc-700'
                data-dd-privacy='mask'
              >
                {addressDetails?.cx_address?.address}
              </span>
            ),
          },
        ]}
      />
      <DataCard
        icon={<ShoppingCart size={16} />}
        heading='Current Assigned Store'
        stats={[
          {
            value: (
              <span
                className='tw-font-medium tw-text-zinc-700'
                data-dd-privacy='mask'
              >
                {addressDetails?.merchant_address?.name}
              </span>
            ),
          },
        ]}
      />
    </div>
  );
};

export default function ServiceableStoresComp(props) {
  const client = useClient();
  const { orderId, updateAddressServiceability = null, cartId, userId } = props;
  const [addressDetails, setAddressDetails] = useState();
  const [cxPinPoint, setCxPinPoint] = useState({});
  const [isConfirmingLocationUpdate, setIsConfirmingLocationUpdate] =
    useState(false);

  const { isLoading: addressLoading, fetching: isAddressFetching } =
    useGetAddressDetails(orderId, {
      onSuccess: (res) => {
        setAddressDetails(res);
      },
      cacheTime: 0,
    });

  const { data: storesInfo, error } = useGetServeciableStores(
    {
      orderId: orderId,
      cxLat: cxPinPoint?.lat,
      cxLng: cxPinPoint?.lng,
    },
    {
      enabled: Boolean(cxPinPoint?.lat && cxPinPoint?.lng),
    },
  );

  const SendUpdateCustomerLocationEvent = ({ success }) => {
    UpdateCustomerLocationEvent({
      [MetricsForJumboEvent.customer_id]: userId,
      [MetricsForJumboEvent.cart_id]: cartId,
      [MetricsForJumboEvent.order_id]: orderId,
      [MetricsForJumboEvent.feature_meta]: {
        success: success,
        isAddressServiceable: curr_merchant_serviceable,
        distance: curr_merchant_distance,
        reason: reason,
        lat: cxPinPoint.lat,
        lng: cxPinPoint.lng,
        isCancellationFlow: !!updateAddressServiceability,
      },
    });
  };

  //mutation for updating customer location
  const {
    mutate: updateCustomerLocation,
    isLoading: isUpdatingCustomerLocation,
  } = useMutationCall(
    {
      onSuccess: (res) => {
        if (res?.success === false) {
          toast.error(res?.message ?? 'Sorry, we could not update the address');
        } else {
          toast.success(res?.message ?? 'Address updated successfully');
          setIsConfirmingLocationUpdate(false);
        }
        SendUpdateCustomerLocationEvent({ success: res?.success });
      },
      onError: () => {
        SendUpdateCustomerLocationEvent({ success: false });
      },
    },
    updateCustomerLocationEndpoint,
    ['update_customer_location', orderId],
    {
      doNotShowSuccessToast: true,
    },
    (data) => client(updateCustomerLocationEndpoint, { data }),
  );

  const {
    curr_merchant_serviceable,
    curr_merchant_distance,
    reason,
    show_update_address,
  } = storesInfo || {};

  useEffect(() => {
    if (cxPinPoint?.lat && cxPinPoint?.lng) {
      // Log the event for checking store serviceability
      CheckStoreServiceabilityInputEvent({
        [MetricsForJumboEvent.customer_id]: userId,
        [MetricsForJumboEvent.cart_id]: cartId,
        [MetricsForJumboEvent.order_id]: orderId,
        [MetricsForJumboEvent.feature_meta]: {
          isAddressServiceable: curr_merchant_serviceable,
          distance: curr_merchant_distance,
          reason: reason,
          lat: cxPinPoint.lat,
          lng: cxPinPoint.lng,
          isCancellationFlow: !!updateAddressServiceability,
        },
      });

      updateAddressServiceability?.({
        isServiceable: curr_merchant_serviceable,
        distance: curr_merchant_distance,
        reason: reason,
        error: error,
      });
    }
  }, [storesInfo, cxPinPoint?.lat, cxPinPoint?.lng, error]);

  const confirmUpdateLocation = () => {
    setIsConfirmingLocationUpdate(true);
  };

  const cancelLocationUpdate = () => {
    setIsConfirmingLocationUpdate(false);
  };

  const handleUpdateLocation = () => {
    if (!cxPinPoint?.lat || !cxPinPoint?.lng) return;

    const payload = {
      lat: cxPinPoint.lat,
      lon: cxPinPoint.lng,
      order_id: orderId,
    };
    updateCustomerLocation(payload);
  };

  return (
    <div className='tw-w-full tw-mt-0 tw-flex tw-flex-col tw-gap-6'>
      <LocationSearchBox setCxPinPoint={setCxPinPoint} />

      {addressLoading || isAddressFetching ? (
        <Skeleton
          rows={[
            { cols: 1, height: '7rem' },
            { cols: 1, height: '7rem' },
          ]}
        />
      ) : (
        <div className='tw-flex tw-flex-col tw-gap-10'>
          {/* Current Customer Address and assigned store address */}
          <AddressDetails addressDetails={addressDetails} />

          {/* Serviceability Status */}
          {storesInfo && (
            <div className='tw-flex tw-flex-col tw-gap-4 tw-items-end'>
              <div className='tw-text-400 tw-font-medium tw-flex tw-items-center tw-gap-2 tw-w-full'>
                <Bike size={18} />
                Serviceability Status
              </div>
              <div
                className={`tw-py-5 tw-px-5  tw-border-solid tw-rounded-2xl tw-border-[1px] ${
                  curr_merchant_serviceable
                    ? 'tw-border-green-500 tw-bg-green-100/30 tw-text-base-green'
                    : 'tw-border-red-400 tw-bg-red-100/30 tw-text-red-600'
                }  tw-text-300 tw-font-medium tw-flex tw-flex-col tw-gap-2 tw-w-full`}
              >
                <div className='tw-flex tw-items-center tw-justify-between'>
                  <div className='tw-text-[1.5rem] tw-capitalize'>
                    This address is{' '}
                    <span
                      className={`tw-text-[1.5rem] ${
                        curr_merchant_serviceable
                          ? 'tw-text-base-green'
                          : 'tw-text-red-600'
                      }`}
                    >
                      {curr_merchant_serviceable ? '' : 'not '}
                      serviceable.
                    </span>
                  </div>
                  {curr_merchant_distance && (
                    <div data-dd-privacy='mask'>
                      {curr_merchant_distance} KMs
                    </div>
                  )}
                </div>
                {reason && (
                  <div className='tw-text-grey-600 tw-font-medium'>
                    Reason: {reason}
                  </div>
                )}
              </div>
              {!isConfirmingLocationUpdate &&
                show_update_address &&
                curr_merchant_serviceable && (
                  <Button
                    title={'Update Drop Location'}
                    size='medium'
                    className='tw-mt-2'
                    onClick={confirmUpdateLocation}
                  />
                )}
              {isConfirmingLocationUpdate &&
                curr_merchant_serviceable &&
                show_update_address && (
                  <LocationUpdateConformationUI
                    cancelLocationUpdate={cancelLocationUpdate}
                    handleUpdateLocation={handleUpdateLocation}
                    isUpdatingCustomerLocation={isUpdatingCustomerLocation}
                  />
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
